import { CommonModule } from "@angular/common";
import { Component, EventEmitter, OnInit, Output, signal, WritableSignal, output, inject, ElementRef, ViewChild, OnDestroy } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { IndicationQuoteStepService } from "../../services/indicationQuoteStep.service";
import { forkJoin, map, Subscription} from 'rxjs';
import { IndicationInceptionStatus, MessageId, ToastType } from "../../enums/config.enum";
import { IndicationRagDetailsResponse, QuoteRagDetailsResponse, QuoteRiskSummary, SaveIndicationAndHomeResponse } from "../../models/RequestQuote";
import { AuthService } from "../../auth/auth.service";
import { GetUserDetails } from "../../models/sessionStorage.model";
import { ToastService } from "../../services/toast.service";
import { Router } from "@angular/router";
import { RequestQuoteReferralStepComponent } from "../request-quote-referral-step/request-quote-referral-step.component";
import { AcceptQuoteStepComponent } from "../accept-quote-step/accept-quote-step.component";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "../utils/confirmation-dialog/confirmation-dialog.component";
import { RequestQuoteService } from "../../services/requestQuote.service";
import { BrokerQuoteService } from "../../services/brokerQuote.service";
import { MessageCenterService } from "../../services/messageCenter.service";
import { PolicyAdministrationService } from "../../services/policyAdministration.service";
import { ReferralDialogComponent } from "../utils/referral-dialog/referral-dialog.component";
import { QuoteReferredDeclinedComponent } from "../utils/quote-referred-declined/quote-referred-declined.component";
import { EqbdApprovalDialogComponent } from "../utils/eqbd-approval-dialog/eqbd-approval-dialog.component";
import { sign } from "crypto";
import { SystemAdminService } from "../../services/system-admin.service";
import { NavigationHandlerService } from "../../services/navigationHandler.service";
import { InceptionStatusResponse } from "../../models/QuoteList";
import { PropertyService } from "../../services/property.service";

@Component({
    selector: 'app-indication-quote-step',
    standalone: true,
    imports: [MatExpansionModule, MatSnackBarModule, MatDialogModule, CommonModule, FormsModule, ReactiveFormsModule, 
        RequestQuoteReferralStepComponent, AcceptQuoteStepComponent],
    templateUrl: './indication-quote-step.component.html',
    styleUrl: './indication-quote-step.component.scss',
})
export class IndicationQuoteStepComponent implements OnInit, OnDestroy{
    role: number | null = null;
    isReadOnly: WritableSignal<boolean> = signal(false);
    step = signal(0);
    requestQuoteStep = signal(2);
    QuoteReferredApprovedStep = signal(3);
    proceedToRequestQuoteStep = signal(false);
    isIndicationAmendByUWAdmin = signal(false);
    amendIndicationEmitter = output<boolean>();
    requestQuoteEmitter = output<number>();
    saveIndicationHomeEmitter = output();
    isReferralEmitter = output();
    dataEmitter = output<boolean>(); 
    targetPremiumEmitter = output<boolean>();
    amendQuoteEmitter = output<boolean>();
    indicationQuoteDetailsEmitter = output<any>();
    indicationRiskSummaryDetailsEmitter = output<any>();
    policyRiskSummaryDetailsEmitter = output<any>();
    submitReferralEmitter = output<boolean>();
    clearanceCheckEmitter = output<boolean>();
    clearanceNameAddressCheckEmitter = output<boolean>();
    authService = inject(AuthService);
    router = inject(Router);
    dialog = inject(MatDialog);
    indicationQuoteService = inject(IndicationQuoteStepService);
    brokerQuoteService = inject(BrokerQuoteService);
    messageCenterService = inject(MessageCenterService);
    indicationQuoteDetails: any = signal(null);
    quoteFeedbackMessage = signal('');
    userId = signal<string | null>('');
    indicationId = signal<string | null>('');
    quoteId = signal<string | null>('');
    branchId = signal<string | null>('');
    declineMessage: any = null;
    showNotification: boolean = false;
    isIndicationReferralLock = signal<boolean>(false);
    isReferralLock = signal<boolean>(false);
    maxAmendLock = signal<boolean>(false);
    maxAmendLimit = signal<boolean>(false);
    targetPremiumLock = signal<boolean>(false);
    dtcRuleLock = signal<boolean>(false);
    quoteAmendLock = signal<boolean>(false);
    indicationReferredApprovedLock = signal<boolean>(false);
    indicationReferredDeclinedLock = signal<boolean>(false);
    ClearanceNameCheckLock = signal<boolean>(false);
    storageData = signal<GetUserDetails | null>(null);
    toastService = inject(ToastService);
    requestQuoteService = inject(RequestQuoteService);
    policyService = inject(PolicyAdministrationService);
    propertyService = inject(PropertyService);
    indicationToQuoteDataSubs = new Subscription();
    systemAdminService = inject(SystemAdminService);
    InceptionStatusList = signal<InceptionStatusResponse[]>([]);      
    eqbdReferralDetails = signal<any[]>([]);
    @Output() newItemEvent = new EventEmitter<string>();
    navigationHandler = inject(NavigationHandlerService);
    navLogoClickSubscription = new Subscription();

    ngOnInit(): void {
        this.authService.waitForSessionCachesRestored().subscribe(() => {
            this.initializeAfterCacheHydration();
        });
    }

    private initializeAfterCacheHydration(): void {
        
        this.navLogoClickSubscription = this.navigationHandler.logoClick$.subscribe(() => {
            this.onSaveIndicationHomeAction();
        });
        this.targetPremiumEmitter.emit(true);
        const storageData = this.authService.getUserDetailsFromStorage();
        this.quoteId.set(storageData.quoteId);
        this.getQuoteConfig();
        this.getInceptionStatusList();

        const IndicationApprovedReferred = sessionStorage.getItem("AmendDisable");
        if(IndicationApprovedReferred === "true"){
            this.indicationReferredApprovedLock.set(true)
        }else{
            this.indicationReferredApprovedLock.set(false)
        }
        const IndicationDeclineReferred = sessionStorage.getItem("requestQuoteDisable");
        if (IndicationDeclineReferred === "true"){
            this.indicationReferredDeclinedLock.set(true)
        }else{
            this.indicationReferredDeclinedLock.set(false);
        }

        if(!this.authService.isPolicyType()){
            this.fetchIndicationToQuoteData();
        }

        const isreferral = Number(sessionStorage.getItem("isreferral"))
        if(isreferral === 1){
            this.isReferralLock.set(true)
        }else{
            this.isReferralLock.set(false)
        }

        this.indicationToQuoteDataSubs = this.requestQuoteService.getIndicationToQuoteStepData().subscribe({
            next:(response: boolean) => {
                if(response && !this.authService.isPolicyType()){
                    this.fetchIndicationToQuoteData();
                }
            }
        });
    }

    isUwAdmin(): boolean {
        return this.role === 2;
    }
      
    isSysAdmin(): boolean {
        return this.role === 3;
    }
      
    isBroker(): boolean {
        return this.role ===1;
    }

    isIndicationType(): boolean {
        const quoteType = this.storageData()?.quoteType;
        return quoteType === "indication";
    }    

    onIndicationConverted(data: boolean){
        if(data){
            this.proceedToRequestQuoteStep.set(true);
            this.fetchQuoteRiskSummaryData();
        }
    }

    onConfirmationPopup(message: string){
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: { message: message }
        });
    
        return dialog.afterClosed()
    }

    
  fetchQuoteRiskSummaryData() {
    this.indicationQuoteService.getQuoteRiskSummary(Number(this.quoteId())).subscribe({
      next: (response: QuoteRiskSummary) => {
        if(response){
           this.indicationRiskSummaryDetailsEmitter.emit(response);
           const statusList = this.brokerQuoteService.getStatusListFromStorage();
           if(statusList){
             this.brokerQuoteService.setStatusId(statusList[response.status]?.gencodeId); // set statusId
           }
        }
      },
      error: (error: any) => {
        
        this.toastService.addNotification({message: "System error. Failed to retrieve risk summary data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
      }
    });
  }

    fetchIndicationToQuoteData(){
        const storageData = this.authService.getUserDetailsFromStorage();
        this.storageData.set(storageData);
        this.role = storageData.userRole ? Number(storageData.userRole) : null;
        this.userId.set(String(storageData?.userDetails?.userId));
        this.indicationId.set(storageData.indicationId);
        this.branchId.set(storageData.branchId);
        this.isIndicationAmendByUWAdmin.set(storageData?.isAmendByUWAdmin === "1" ? true : false); // for showing submit button
        this.loadEQBDReferralDetailsForUwAdmin();
        this.handleSaveStep();
        if(this.isIndicationType()){
            // Get Rag Details for Indication
           if(this.indicationId()){
               this.getIndicationRating(this.indicationId());
           } 
       }else{
           // Get Rag Details for Quote 
           this.quoteId.set(storageData.quoteId); 
           
           const quoteId = this.quoteId();
           if(quoteId) {
               this.getQuoteRating(quoteId);
           }
       }
    }
     
    amendIndicationBrokerHandler() {
        this.indicationQuoteService.amendIndicationByBroker(Number(this.indicationId()), Number(this.branchId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    sessionStorage.setItem("isReview", JSON.stringify(false));
                    this.dataEmitter.emit(true);
                }
            },
            error: (error: any) => {
                
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                this.messageCenterService.onUpdateSystemUserMessages(true); // update message center get system generated messages
            }
        });
    }

    amendQuoteBrokerHandler() {
        this.indicationQuoteService.amendQuoteByBroker(Number(this.quoteId()), Number(this.branchId()))
        .subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    sessionStorage.setItem("isReview", JSON.stringify(false));
                    this.amendQuoteEmitter.emit(true);
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                this.messageCenterService.onUpdateSystemUserMessages(true); // update message center get system generated messages
            }
        })
    }

    private buildUWAdminAmendmentPayload(transactionType: 'indication' | 'quote') {
        const transactionId = transactionType === 'indication'
            ? Number(this.indicationId())
            : Number(this.quoteId());
        const versionNo = Number(sessionStorage.getItem('versionNo') || 0);
        const userId = Number(this.userId() || this.authService.getUserDetailsFromStorage()?.userDetails?.userId || 0);

        return {
            transactionType,
            transactionId,
            isAmended: 1,
            amendmentVersion: versionNo,
            amendedByUserId: userId,
            amendedByUserType: "UWADMIN",
            amendedDate: new Date().toISOString()
        };
    }

    private saveUWAdminAmendment(transactionType: 'indication' | 'quote', onComplete: () => void) {
        const payload = this.buildUWAdminAmendmentPayload(transactionType);
        this.indicationQuoteService.SaveUWAdminAmendment(payload).subscribe({
            next: () => {
                onComplete();
            },
            error: () => {
                this.toastService.addNotification({message: "System error. Failed to save amendment details. Please exit and contact help@AbacusAU.com.", type: ToastType.Error});
                this.messageCenterService.onUpdateSystemUserMessages(true);
            }
        });
    }

    amendIndicationHandler() {
        this.indicationQuoteService.amendIndicationByUWAdmin(Number(this.indicationId()), Number(this.userId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    this.saveUWAdminAmendment('indication', () => {
                        sessionStorage.setItem("isAmendByUWAdmin", "1"); // hide approve & decline btn & enable submit btn
                        sessionStorage.setItem("isReview", JSON.stringify(false));
                        sessionStorage.setItem("RagCall","true");
                        this.dataEmitter.emit(true);  
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                this.messageCenterService.onUpdateSystemUserMessages(true); // update message center get system generated messages
            }
        });
    }

    amendQuoteHandler() {
        this.indicationQuoteService.amendQuoteByUWAdmin(Number(this.quoteId()), Number(this.userId()))
        .subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    this.saveUWAdminAmendment('quote', () => {
                        sessionStorage.setItem("isAmendByUWAdmin", "1"); // hide approve & decline btn & enable submit btn
                        sessionStorage.setItem("isReview", JSON.stringify(false));
                        sessionStorage.setItem("RagCall","true");
                        this.amendQuoteEmitter.emit(true);
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                this.messageCenterService.onUpdateSystemUserMessages(true); // update message center get system generated messages
            }
        })
    }

    approveIndicationHandler() {
        this.saveApproveStepForUwAdminAndPos("Indication", String(this.indicationId()));
        this.indicationQuoteService.approveIndication(Number(this.indicationId()), Number(this.userId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    this.resetClearanceAddressEdited(() => {
                        const msg =  response?.message || "Indication referral approved successfully";
                        this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                        this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                        this.router.navigate(['/quote']);
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    onSetStatusId(statusName: string) {
        const getInprogressStatusList = this.InceptionStatusList().filter((status) => status.description === statusName);
        
        if(getInprogressStatusList.length > 0){
        this.brokerQuoteService.setInceptionStatusId(getInprogressStatusList[0]?.code); // Indication inception Referral Inprogress Code
        }
    }

    ApproveDeclineInceptionReferral(){
        const quoteId = sessionStorage.getItem("quoteId");
        const branchId = sessionStorage.getItem("branchId");   
        const Status = this.brokerQuoteService.getInceptionStatusCode();
        const versionNo = sessionStorage.getItem("versionNo")
    
        this.indicationQuoteService.SubmitReferralInceptionDate(Number(quoteId),Number(branchId),String(Status),Number(versionNo)).subscribe({
        next: (response) => {
            if(response){
                
            }
        },
        error: (error) => {
        }
        })  
    }

    approveQuoteHandler() {
        this.saveApproveStepForUwAdminAndPos("Quote", String(this.quoteId()));
        let irStatus = String(sessionStorage.getItem("inceptionStatus"))
        if (irStatus === "IDR"){
            this.onSetStatusId(IndicationInceptionStatus.STATUS_IDRA);
        }        
        this.indicationQuoteService.approveQuote(Number(this.quoteId()), Number(this.userId()))
        .subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    if(irStatus === "IDR"){this.ApproveDeclineInceptionReferral()}
                    this.resetClearanceAddressEdited(() => {
                        const msg =  response?.message || "Quote referral approved successfully.";
                        this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                        this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                        this.router.navigate(['/quote']);
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    denyIndicationHandler() {
        this.handleSaveStep();
        this.indicationQuoteService.denyIndication(Number(this.indicationId()), Number(this.userId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    const msg =  response?.message || "Indication referral denied successfully." ;
                    this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                    this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                    this.router.navigate(['/quote']);
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    denyQuoteHandler() {
        this.handleSaveStep();
           let irStatus = String(sessionStorage.getItem("inceptionStatus"))
        if (irStatus === "IDR"){
            this.onSetStatusId(IndicationInceptionStatus.STATUS_IDRD);
        }
        this.indicationQuoteService.denyQuote(Number(this.quoteId()), Number(this.userId()))
        .subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    if(irStatus === "IDR"){this.ApproveDeclineInceptionReferral()}
                    const msg =  response?.message || "Quote referral denied.";
                    this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                    this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                    this.router.navigate(['/quote']);
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    submitIndicationHandler() {
        this.saveApproveStepForUwAdminAndPos("Indication", String(this.indicationId()));
        this.indicationQuoteService.submitIndication(Number(this.indicationId()), Number(this.userId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    this.resetClearanceAddressEdited(() => {
                        const msg =  response?.message || "Indication Changes submitted successfully." ;
                        this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                        this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                        this.router.navigate(['/quote']);
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "Failed to submit indication", type: ToastType.Error}); // error notification
            }
        });
    }

    submitQuoteHandler() {
        this.saveApproveStepForUwAdminAndPos("Quote", String(this.quoteId()));
        let irStatus = String(sessionStorage.getItem("inceptionStatus"))
        if (irStatus === "IDR"){
            this.onSetStatusId(IndicationInceptionStatus.STATUS_IDRA);
        }  
        this.indicationQuoteService.submitQuote(Number(this.quoteId()), Number(this.userId())).subscribe({
            next: (response: SaveIndicationAndHomeResponse) => {
                if(response && response?.message){
                    if(irStatus === "IDR"){this.ApproveDeclineInceptionReferral()}
                    this.resetClearanceAddressEdited(() => {
                        const msg =  response?.message || "Quote Changes submitted successfully." ;
                        this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                        this.indicationQuoteService.removeIndicationQuoteStorageData(); // clean sessionstorage data
                        this.router.navigate(['/quote']);
                    });
                }
            },
            error: (error: any) => {
                
                this.toastService.addNotification({message: "Failed to submit quote", type: ToastType.Error}); // error notification
            }
        });
    }

    onBrokerAmendIndication(): void{
        const msg = this.isIndicationType() ? "Are you sure you want to amend this Indication?" :
        "Are you sure you want to amend this Quote?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){        
              this.isIndicationType() ? this.amendIndicationBrokerHandler() : this.amendQuoteBrokerHandler();
            }
        });
    }

    onUWAdminAmendIndication(): void {
        const msg = this.isIndicationType() ? "Are you sure you want to amend this Indication?" :
        "Are you sure you want to amend this Quote?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){        
              this.isIndicationType() ? this.amendIndicationHandler() : this.amendQuoteHandler();
            }
        });
    }

    // OnAmentIndication(): void{
    //     this.amendIndicationEmitter.emit(true);
    // }

    private getEQBDReferralContext(): { id: number; type: string } | null {
        const storageData = this.authService.getUserDetailsFromStorage();
        if (!storageData) {
            return null;
        }
        if (this.isIndicationType()) {
            const indicationId = Number(storageData?.indicationId ?? 0);
            return indicationId ? { id: indicationId, type: 'indication' } : null;
        }
        const quoteId = Number(storageData?.quoteId ?? 0);
        return quoteId ? { id: quoteId, type: 'quote' } : null;
    }

    private loadEQBDReferralDetailsForUwAdmin(): void {
        if (!this.isUwAdmin() || this.authService.isPolicyType()) {
            return;
        }
        const context = this.getEQBDReferralContext();
        if (!context) {
            return;
        }
        this.indicationQuoteService.GetEQBDReferralDetails(context.id, context.type).subscribe({
            next: (response: any) => {
                const records = Array.isArray(response?.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);
                this.eqbdReferralDetails.set(records);
            },
            error: () => {
                this.eqbdReferralDetails.set([]);
            }
        });
    }

    private updateEQBDReferralAndApprove(isEQBDAvailabe: number): void {
        const referrals = this.eqbdReferralDetails();
        if (!Array.isArray(referrals) || referrals.length === 0) {
            this.isIndicationType() ? this.approveIndicationHandler() : this.approveQuoteHandler();
            return;
        }
        const selectedAvailability = Number(isEQBDAvailabe) === 1 ? 1 : 0;
        const latestVersionNo = referrals.reduce((max: number, item: any) => {
            const version = Number(item?.versionNo);
            return isNaN(version) ? max : Math.max(max, version);
        }, 0);
        const eqbdreferral = referrals.map((referral: any) => {
            const version = Number(referral?.versionNo);
            const isLatestVersion = latestVersionNo > 0
                ? version === latestVersionNo
                : referral === referrals[referrals.length - 1];
            if (!isLatestVersion) {
                return referral;
            }
            return {
                ...referral,
                isEQBDAvailabe: selectedAvailability,
                updatedUserId: Number(this.userId())
            };
        });
        const payload = { eqbdreferral };

        this.indicationQuoteService.UpdateEQBDReferralApproval(payload).subscribe({
            next: () => {
                this.eqbdReferralDetails.set(eqbdreferral);
                this.approveClearanceCheckIfNeeded(() => {
                    this.isIndicationType() ? this.approveIndicationHandler() : this.approveQuoteHandler();
                });
            },
            error: () => {
                this.toastService.addNotification({
                    message: "System error. Failed to update EQBD referral approval. Please retry.",
                    type: ToastType.Error
                });
            }
        });
    }
    
    onApproveIndication(): void{
        const currentVersionNo = Number(sessionStorage.getItem("versionNo"));
        const currentVersionReferral = this.eqbdReferralDetails().find(
            (item: any) => Number(item?.versionNo) === currentVersionNo
        );
        const hasEqbdReferral = !!currentVersionReferral && Number(currentVersionReferral?.eqbdReferral ?? 0) === 1;
        if (!hasEqbdReferral) {
            const msg = this.isIndicationType() ? "Are you sure you want to Approve this Indication?" :
            "Are you sure you want to Approve this Quote?";
            this.onConfirmationPopup(msg)
            .subscribe((result: boolean) => {
                if(result){
                   this.approveClearanceCheckIfNeeded(() => {
                       this.isIndicationType() ? this.approveIndicationHandler() : this.approveQuoteHandler();
                   });
                }
            });
            return;
        }

        const msg = "You are about to approve this Indication Referral. Please confirm the approval and specify whether Equipment Breakdown (EQBD) coverage should be available for this submission.";
        const dialog = this.dialog.open(EqbdApprovalDialogComponent, {
            width: '500px',
            maxWidth: '85vw',
            data: { message: msg }
        });
        dialog.afterClosed().subscribe((result: any) => {
            if(result?.approved){
               this.updateEQBDReferralAndApprove(Number(result?.isEQBDAvailabe ?? 0));
            }
        });
    }

    onDeclineIndication(){
        const msg = this.isIndicationType() ? "Are you sure you want to Decline this Indication?" :
        "Are you sure you want to Decline this Quote?"
        this.onConfirmationPopup(msg)
        .subscribe((result: boolean) => {
            if(result){
                this.isIndicationType() ? this.denyIndicationHandler() : this.denyQuoteHandler();
            }
        });
    }

    onSubmitIndication(){
        const currentVersionNo = Number(sessionStorage.getItem("versionNo"));
        const currentVersionReferral = this.eqbdReferralDetails().find(
            (item: any) => Number(item?.versionNo) === currentVersionNo
        );
        const hasEqbdReferral = !!currentVersionReferral && Number(currentVersionReferral?.eqbdReferral ?? 0) === 1;
        if (!hasEqbdReferral) {
            const msg = this.isIndicationType() ? "Are you sure you want to Approve the amended Indication and release back to POS User?" : 
            "Are you sure you want to Approve the amended Quote and release back to POS User?"
            this.onConfirmationPopup(msg)
            .subscribe((result: boolean) => {
                if(result){
                    this.approveClearanceCheckIfNeeded(() => {
                        this.isIndicationType() ? this.submitIndicationHandler() : this.submitQuoteHandler();
                    });
                }
            });

            return;
        }

        const msg = "You are about to approve this Indication Referral. Please confirm the approval and specify whether Equipment Breakdown (EQBD) coverage should be available for this submission.";
        const dialog = this.dialog.open(EqbdApprovalDialogComponent, {
            width: '500px',
            maxWidth: '85vw',
            data: { message: msg }
        });
        dialog.afterClosed().subscribe((result: any) => {
            if(result?.approved){
               this.updateEQBDReferralAndApprove(Number(result?.isEQBDAvailabe ?? 0));
            }
        });
    }
 
  

    showDeclineNotification(message: string) {
        this.declineMessage = message;
        this.showNotification = true;
    
        setTimeout(() => {
          this.showNotification = false;
          this.declineMessage = null;
        }, 5000);
    }


    private isReferralApprovedStatus(): boolean {
        const status = sessionStorage.getItem("statusRagCall");
        return status === "Indication - Referral Approved" || status === "Quote - Referral Approved";
    }

    private hasClearanceEditedFlag(response: any): boolean {
        if (!response) {
            return false;
        }
        const clearanceAddressEdited = Number(response?.clearanceAddressEdited ?? 0) === 1;
        const addresses = Array.isArray(response?.addresses) ? response.addresses : [];
        const hasAddressEdited = addresses.some(
            (addr: any) => Number(addr?.isClearanceAddressEdited ?? 0) === 1
        );
        return clearanceAddressEdited || hasAddressEdited;
    }

    private approveClearanceCheckIfNeeded(onComplete: () => void): void {
        const context = this.getClearanceEntityContext();
        if (!context) {
            onComplete();
            return;
        }

        this.propertyService.GetClearanceCheckFlag(context.id, context.type).subscribe({
            next: (response: any) => {
                if (!this.hasClearanceEditedFlag(response)) {
                    onComplete();
                    return;
                }

                const payload = {
                    id: context.id,
                    type: context.type,
                    isClearanceApprovedByUW: 1
                };
                this.propertyService.ClearanceCheckApproval(payload).subscribe({
                    next: () => onComplete(),
                    error: () => onComplete()
                });
            },
            error: () => onComplete()
        });
    }

    private evaluateRequestQuoteClearanceCheck(): void {
        if (this.isReferralApprovedStatus()) {
            this.OnRequestQuote();
            return;
        }

        const context = this.getClearanceEntityContext();
        if (!context) {
            this.clearanceNameAddressCheckEmitter.emit(true);
            return;
        }

        this.propertyService.GetClearanceCheckFlag(context.id, context.type).subscribe({
            next: (response: any) => {
                if (Number(response?.isClearanceApprovedByUW ?? 0) === 1) {
                    this.OnRequestQuote();
                } else {
                    this.clearanceNameAddressCheckEmitter.emit(true);
                }
            },
            error: () => {
                this.clearanceNameAddressCheckEmitter.emit(true);
            }
        });
    }

    referralClearance(){
        const QuoteReferredDeclined = sessionStorage.getItem("QuoteReferredDeclined")
        if(QuoteReferredDeclined !== "true"){
            this.evaluateRequestQuoteClearanceCheck();
        }else{
            const dialog = this.dialog.open(QuoteReferredDeclinedComponent, {
                data: { message: "The current version is declined by the Underwriting Admin. Proceed with a previous version or amend further." }
            });

            dialog.afterClosed().subscribe((result: boolean) => {
                if(result){
                    this.requestQuoteEmitter.emit(this.QuoteReferredApprovedStep());
                }
              }); 
        }
    }

    private getClearanceEntityContext(): { id: number; type: string } | null {
        const storageData = this.authService.getUserDetailsFromStorage();
        if (!storageData) {
            return null;
        }

        if (this.isIndicationType()) {
            const indicationId = Number(storageData?.indicationId ?? 0);
            return indicationId ? { id: indicationId, type: "indication" } : null;
        }

        const quoteId = Number(storageData?.quoteId ?? 0);
        return quoteId ? { id: quoteId, type: "quote" } : null;
    }

    private resetClearanceAddressEdited(onComplete: () => void): void {
        const context = this.getClearanceEntityContext();
        const userId = Number(this.authService.getUserDetailsFromStorage()?.userDetails?.userId ?? 0);
        if (!context || !userId) {
            onComplete();
            return;
        }

        this.propertyService.UpdateClearanceAddressEdit(context.id, userId, 0, context.type).subscribe({
            next: () => onComplete(),
            error: () => onComplete()
        });
    }

    OnRequestQuote(): void{
        this.acceptUWAdminAmendmentIfNeeded(() => {
            this.continueRequestQuote();
        });
    }

    private continueRequestQuote(): void {
        this.newItemEvent.emit();
        this.indicationQuoteService.requestQuoteSummaryDetails().subscribe({
            next: (response) => {
                if(response){
                    this.handleSaveStep();
                    // If the response is success then move to the next step
                    this.requestQuoteEmitter.emit(this.requestQuoteStep());
                }else{
                    // response is negative show prompt
                    const msg = "This Indication is already quoted, please submit referral";
                    this.toastService.addNotification({message: msg, type: ToastType.Error}); // error notification
                }
            },
            error: (error) => {
                
                // show error message
                this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        })
    }

    private acceptUWAdminAmendmentIfNeeded(onComplete: () => void): void {
        if (!this.isBroker()) {
            onComplete();
            return;
        }

        const transactionType = this.isIndicationType() ? "indication" : "quote";
        const transactionId = this.isIndicationType() ? Number(this.indicationId()) : Number(this.quoteId());
        const amendmentVersion = Number(sessionStorage.getItem("versionNo") || 0);

        if (!transactionId) {
            onComplete();
            return;
        }

        this.indicationQuoteService.GetAmendmentDetails(transactionType, transactionId, amendmentVersion).subscribe({
            next: (response: any) => {
                const status = sessionStorage.getItem("statusRagCall");
                const isReferralApproved =
                    status === "Indication - Referral Approved" ||
                    status === "Quote - Referral Approved";
                const isAmended = Number(response?.data?.isAmended ?? 0) === 1;

                if (!isAmended || !isReferralApproved) {
                    onComplete();
                    return;
                }

                const payload = {
                    transactionType,
                    transactionId,
                    amendmentVersion,
                    isAmendmentAccepted: 1,
                    amendmentAcceptedByUserId: Number(this.userId() || this.authService.getUserDetailsFromStorage()?.userDetails?.userId || 0),
                    amendmentAcceptedDate: new Date().toISOString()
                };

                this.indicationQuoteService.UpdateUWAdminAmendment(payload).subscribe({
                    next: () => onComplete(),
                    error: () => {
                        this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error});
                    }
                });
            },
            error: () => {
                onComplete();
            }
        });
    }

    onReturnToDashboardMTA() {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: { message: "Do you want to return to dashboard?" }
        });
      
        dialog.afterClosed().subscribe((result: boolean) => {
          if(result){
            const isQuoteMTA = sessionStorage.getItem('isQuoteMTA');
            if(isQuoteMTA){
              this.policyService.declineMTAByBroker(Number(this.authService.getUserDetailsFromStorage()?.policyId), true)
              .subscribe({
                next: (response: any) => {
                  if(response){
                    this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
                  }
                },
                error: (error: any) => {
                  
                  this.toastService.addNotification({message: "System error. Failed to save data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
                }
              })
            }else{
              this.indicationQuoteService.redirectToDashboard(); // redirect to dashboard
            }
          }
        }); 
    }

         onGetSystemUserMessage(){ // update message center get system generated messages
    this.messageCenterService.onUpdateSystemUserMessages(true);
  }
    
    handleSaveStep() {
        if(this.authService.isPolicyType()){
            this.onSaveStep("4a", "Policy", String(this.authService.getUserDetailsFromStorage()?.policyId));
        }else{
            if(this.isIndicationType()){
                this.onSaveStep("4a", "Indication", String(this.indicationId()));
            }else{
                this.onSaveStep("4a", "Quote", String(this.authService.getUserDetailsFromStorage()?.quoteId));
            }
        }
    }

    onSaveStep(step: string, type: string, id: string) {
        if (this.isReadOnly()) {
            return;
        }
        this.indicationQuoteService.saveStepForCurrentUser(step, type, id).subscribe({
            next: (response: any) => {
            },
            error: (error: any) => {
                
            }
        })
    }

    saveApproveStepForUwAdminAndPos(type: string, id: string) {
        if (!id || id === 'null' || id === 'undefined') {
            return;
        }

        this.indicationQuoteService.saveStepForCurrentUser("4a", type, id).subscribe({
            next: (response: any) => {
            },
            error: (error: any) => {
            }
        });

        if (this.isUwAdmin()) {
            this.indicationQuoteService.saveStepIndication("4a", type, id).subscribe({
                next: (response: any) => {
                },
                error: (error: any) => {
                }
            });
        }
    }


    onSaveIndicationHomeAction() {
        
        const isreferral = Number(sessionStorage.getItem("isreferral"))
        if(isreferral !== 1){
            if(this.authService.isPolicyType()){
                this.onReturnToDashboardMTA();
            }else{
                if(this.isIndicationType() && this.branchId()) {

                    const payloadRequest: any = {
                        indicationId : this.indicationId(),
                        branchId: this.branchId(),
                        userId: this.userId()
                    }
                    this.saveIndicationHomeEmitter.emit(payloadRequest);
                }else{

                    const payloadRequest: any = {
                        
                        quoteId : this.quoteId(),
                        branchId: this.branchId(),
                        userId: this.userId()
                    }
                    this.saveIndicationHomeEmitter.emit(payloadRequest);
                }
            }
        }else{
            this.router.navigate(['/quote']);
        }
    }


    getIndicationRating(indicationId: string | null) {
            const status = sessionStorage.getItem("statusRagCall");
                    const statusAvailable = sessionStorage.getItem("statusAvailable");              
                    const RagCall = sessionStorage.getItem("RagCall");

                     if ( statusAvailable !== "true" || RagCall === "true"){
                        this.indicationQuoteService.getIndicationRating(Number(indicationId), Number(this.userId())).subscribe({
                            next: (response) => {
                                if(response){
                                    this.indicationQuoteDetails.set(response);                                    
                                    this.indicationQuoteDetailsEmitter.emit(response);                                    
                                    this.fetchIndicationRagDetails(indicationId)
                                }
                            },
                            error: (error) => {
                                
                            }                            
                        });                
                }else{
                    this.getRagData();

                }
        
    }

    getRagData() {
           
            let type: string;
            let id : number;

            if (this.authService?.getUserDetailsFromStorage()?.isPolicyType) {
                type = 'Policy';
                id = Number(this.authService.getUserDetailsFromStorage()?.policyId);
            } else if (this.isIndicationType()) { 
                type = 'Indication';
                id = Number(this.authService.getUserDetailsFromStorage()?.indicationId)
            } else {
                type = 'Quote';
                id = Number(this.authService.getUserDetailsFromStorage()?.quoteId)
            }

            this.indicationQuoteService.getPolicyRAG(id, type).subscribe(
                response => {
                    if(this.isIndicationType()){
                        this.indicationQuoteDetails.set(response);                                    
                        this.indicationQuoteDetailsEmitter.emit(response);                                    
                        this.fetchIndicationRagDetails((this.authService.getUserDetailsFromStorage()?.indicationId));   
                    }else{
                        this.indicationQuoteDetails.set(response);
                        this.indicationQuoteDetailsEmitter.emit(response);
                        this.fetchQuoteRagDetails(this.authService.getUserDetailsFromStorage()?.quoteId);
                    }           
                },
                error => {
                }
            );
            
        }


    fetchIndicationRagDetails(indicationId: string | null){
        forkJoin([
            this.indicationQuoteService.getIndicationRiskSummary(Number(indicationId)),
            this.indicationQuoteService.getQuoteFeedbackMessage(MessageId.QuoteFeedbackDefault)
          ]).pipe(
            map(([ indicationRiskSummary, quoteFeedbackMessage]) => {
              return { indicationRiskSummary, quoteFeedbackMessage};
            })
          ).subscribe({
            next: (response: IndicationRagDetailsResponse) => {
                const { indicationRiskSummary, quoteFeedbackMessage } = response;
                if(indicationRiskSummary ){
                    this.indicationRiskSummaryDetailsEmitter.emit(indicationRiskSummary);
                    this.quoteFeedbackMessage.set(quoteFeedbackMessage.messageDescription);
                    indicationRiskSummary.status === "Indication - Referral In Progress" ? this.isIndicationReferralLock.set(true) : this.isIndicationReferralLock.set(false)    
                    const targetPremium =  indicationRiskSummary.totalPremium;
                    if (targetPremium <= 999 || targetPremium >= 99999){
                        this.targetPremiumLock.set(true)
                    }else {
                        this.targetPremiumLock.set(false)
                    }             
                    const statusList = this.brokerQuoteService.getStatusListFromStorage();
                    if(statusList){
                      this.brokerQuoteService.setStatusId(statusList[indicationRiskSummary.status]?.gencodeId); // set statusId
                    }
                     this.onGetSystemUserMessage();
                }
            },
            error: (error) => {
                
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
          })
    }

 

    getVersionNo(){
        const versionNo = Number(sessionStorage.getItem('versionNo'));
        return versionNo
    }

    getQuoteRating(quoteId: string | null) {
         const status = sessionStorage.getItem("statusRagCall");
        const statusAvailable = sessionStorage.getItem("statusAvailable");              
        const RagCall = sessionStorage.getItem("RagCall");

        if ( statusAvailable !== "true" || RagCall === "true"){
        this.indicationQuoteService.getQuoteRating(Number(quoteId), Number(this.userId())).subscribe({
            next: (response) => {
                if(response){
                    this.indicationQuoteDetails.set(response);
                    this.indicationQuoteDetailsEmitter.emit(response);
                    this.fetchQuoteRagDetails(quoteId)
                }
            },
            error: (error) => {
                
            }
        });
    }else{
        this.getRagData()
        }
    
}
   
    fetchQuoteRagDetails(quoteId: string | null){
        forkJoin([
            this.indicationQuoteService.getQuoteRiskSummary(Number(quoteId)),
            this.indicationQuoteService.getQuoteFeedbackMessage(MessageId.QuoteFeedbackDefault)
          ]).pipe(
            map(([ quoteRiskSummary, quoteFeedbackMessage]) => {
              return { quoteRiskSummary, quoteFeedbackMessage};
            })
          ).subscribe({
            next: (response: QuoteRagDetailsResponse) => {
                const { quoteRiskSummary, quoteFeedbackMessage } = response;
                if(quoteRiskSummary ){
                    this.indicationRiskSummaryDetailsEmitter.emit(quoteRiskSummary);
                    this.quoteFeedbackMessage.set(quoteFeedbackMessage.messageDescription);
                    quoteRiskSummary.indicationReferralLock === 1 ? this.isIndicationReferralLock.set(true) : this.isIndicationReferralLock.set(false);
                    quoteRiskSummary.status === "Quote - Referral In Progress" ? this.isIndicationReferralLock.set(true) : this.isIndicationReferralLock.set(false)    
                    const targetPremium =  quoteRiskSummary.totalPremium;
                    if (targetPremium <= 999 || targetPremium >= 99999){
                        this.targetPremiumLock.set(true)
                    }else {
                        this.targetPremiumLock.set(false)
                    }      
                    const versionNo = this.getVersionNo()
                    versionNo === 4 ? this.quoteAmendLock.set(true) : this.quoteAmendLock.set(false)
                    this.proceedToRequestQuoteStep.set(true); // Request Quote btn enable
                    const statusList = this.brokerQuoteService.getStatusListFromStorage();
                    if(statusList){
                      this.brokerQuoteService.setStatusId(statusList[quoteRiskSummary.status]?.gencodeId); // set statusId
                    }
                     this.onGetSystemUserMessage();
                }
            },
            error: (error) => {                
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    getPolicyRating() {
        this.indicationQuoteService.getPolicyRating(Number(this.authService.getUserDetailsFromStorage()?.policyId)).subscribe({
            next: (response) => {
                if(response){
                    this.indicationQuoteDetails.set(response);
                    this.indicationQuoteDetailsEmitter.emit(response);
                    this.fetchPolicyRagDetails()
                }
            },
            error: (error) => {
                
            }
        });    
    }

    fetchPolicyRagDetails(){
        forkJoin([
            this.policyService.getPolicyRiskSummary(Number(this.authService.getUserDetailsFromStorage()?.policyId)),
            this.indicationQuoteService.getQuoteFeedbackMessage(MessageId.QuoteFeedbackDefault)
        ]).pipe(
            map(([policyRiskSummary, quoteFeedbackMessage]) => {
              return {policyRiskSummary, quoteFeedbackMessage};
            })
        ).subscribe({
            next: (response: any) => {
                const { policyRiskSummary, quoteFeedbackMessage } = response;
                if(policyRiskSummary ){
                    this.policyRiskSummaryDetailsEmitter.emit(policyRiskSummary);
                    this.quoteFeedbackMessage.set(quoteFeedbackMessage.messageDescription);
                    this.proceedToRequestQuoteStep.set(true); // Request Quote btn enable
                    const statusList = this.brokerQuoteService.getStatusListFromStorage();
                    if(statusList){
                      this.brokerQuoteService.setStatusId(statusList[policyRiskSummary.status]?.gencodeId); // set statusId
                    }
                }
            },
            error: (error) => {
                
                this.toastService.addNotification({message: "System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.", type: ToastType.Error}); // error notification
            }
        });
    }

    fetchIndicationRiskSummary(indicationId: string | null) {
        if(indicationId){
            this.indicationQuoteService.getIndicationRiskSummary(Number(indicationId)).subscribe({
                next: (response) => {
                    if(response){
                        this.indicationRiskSummaryDetailsEmitter.emit(response);
                        const statusList = this.brokerQuoteService.getStatusListFromStorage();
                        if(statusList){
                          this.brokerQuoteService.setStatusId(statusList[response.status]?.gencodeId); // set statusId
                        }
                    }
                },
                error: (error) => {
                    
                }
            });
        }
    }

    onSubmitReferral(){
        this.indicationQuoteService.submitReferral().subscribe({
            next: (response) => {
                if(response){
                    this.submitReferralEmitter.emit(true);
                }   
            },
            error: (error) => {
                
            }
        })
    }

    submitReferral(){
      if(this.isIndicationType()){
        this.submitIndicationReferral()
      }else{
        this.submitQuoteReferral()
      }
      this.handleSaveStep();
    } 
    
    submitIndicationReferral(){
        this.indicationQuoteService.submitReferal(Number(this.indicationId()),Number(this.branchId())).subscribe({
            next: (response) => {
                if(response){
                    this.toastService.addNotification({message: "Your referral has been successfully submitted", type: ToastType.Success}); 
                    this.indicationQuoteService.removeIndicationQuoteStorageData()
                    this.router.navigate(['/quote']);
                   
                }
            },
            error: (error) => {
                
            }
            })
        } 

    submitQuoteReferral(){
        this.indicationQuoteService.submitReferralQuote(Number(this.quoteId()),Number(this.branchId())).subscribe({
            next: (response) => {
                if(response){
                    this.toastService.addNotification({message: "Your referral has been successfully submitted", type: ToastType.Success}); 
                    this.indicationQuoteService.removeIndicationQuoteStorageData()
                    this.router.navigate(['/quote'])
                }
            },
            error: (error) => {
                
            }
            })
        } 
    
        getQuoteConfig(){
            this.systemAdminService.getQuoteConfig().subscribe({
            next: (response: any) => {
                sessionStorage.setItem("maxVersionCount",response[0].versionMaxCount)
                 const versionNo = Number(sessionStorage.getItem('versionNo'))
                 const maxVersionCount = Number(response[0].versionMaxCount)
                 const status = sessionStorage.getItem("statusRagCall");
                    if(!this.isUwAdmin() ){
                        if(status !== "Indication - Referral Approved" && status !== "Quote - Referral Approved"){
                            if (versionNo > maxVersionCount){
                            this.maxAmendLock.set(true)   
                            this.maxAmendLimit.set(true)                         
                            return
                            }else{
                                this.maxAmendLock.set(false)
                                this.maxAmendLimit.set(false)
                            }
                        }else{
                            if (versionNo > maxVersionCount){
                            this.maxAmendLimit.set(true)                            
                            return
                            }else{
                                this.maxAmendLimit.set(false)
                            }
                        }
                        
                    }
            },
            error: (error: any) => {

            }
            })
        }
        
   getInceptionStatusList(){
    const irStatus = String(sessionStorage.getItem("inceptionStatus"))
      
                this.indicationQuoteService.InceptionStatusList().subscribe({
                next: (response: any) => {
                    this.InceptionStatusList.set(response);
                    this.brokerQuoteService.setInceptionStatusList(response);
                },
                error: (error: any) => {

                }
                })
            
    }


        
    ngOnDestroy(): void {
        if(this.indicationToQuoteDataSubs){
            this.indicationToQuoteDataSubs.unsubscribe();
        }
        if(this.navLogoClickSubscription){
            this.navLogoClickSubscription.unsubscribe();
        }
    }
}

